import { Link, useLocation } from "react-router-dom";
import { CollectionsMenu } from "./CollectionsMenu";
import { useTranslation } from "react-i18next";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useNavigationActions } from "../../hooks/useNavigationActions";
import { useRef } from "react";
import { usePlatform } from "../../hooks/usePlatform";

export const NavigationMenu = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { triggerAddBookmark, triggerCreateCollection } =
    useNavigationActions();
  const buttonRefs = useRef([]);
  const { formatShortcut } = usePlatform();

  const MENU_ITEMS = [
    {
      name: t("sidebar.menu.home", "Home"),
      url: "/home",
      icon: "bi-house",
    },
    {
      name: t("sidebar.menu.bookmarks"),
      url: "/",
      icon: "bi-bookmarks",
      addText: t("bookmarks.addBookmark"),
      shortcut: formatShortcut("B"),
      addAction: triggerAddBookmark,
    },
    // Smart Views: query-param destinations for the two most-used filters, so
    // starring and the reading list finally have a home in the nav.
    {
      name: t("bookmarks.views.favorites", "Favorites"),
      url: "/?favorite=true",
      icon: "bi-star",
    },
    {
      name: t("bookmarks.views.readingList", "Reading list"),
      url: "/?read=false",
      icon: "bi-eyeglasses",
    },
    {
      name: t("sidebar.menu.collections"),
      url: "/collections",
      icon: "bi-collection",
      addText: t("collections.addCollection"),
      shortcut: formatShortcut("U"),
      addAction: triggerCreateCollection,
    },
  ];

  // Features that otherwise hide in Settings or behind the search box.
  const DISCOVER_ITEMS = [
    {
      name: t("sidebar.menu.ask", "Ask AI"),
      url: "/ask",
      icon: "bi-stars",
    },
    {
      name: t("sidebar.menu.rediscover", "Rediscover"),
      url: "/rediscover",
      icon: "bi-arrow-clockwise",
    },
  ];

  // Active-state check that understands query-param Smart Views: "/" is only
  // active as "All bookmarks" when no view filter is set, while "/?favorite=true"
  // matches when that param is present.
  const isActive = (url) => {
    const [path, query = ""] = url.split("?");
    if (location.pathname !== path) return false;
    const current = new URLSearchParams(location.search);
    if (!query) {
      return (
        !current.get("favorite") &&
        !current.get("read") &&
        !current.get("tag")
      );
    }
    for (const [key, value] of new URLSearchParams(query)) {
      if (current.get(key) !== value) return false;
    }
    return true;
  };

  return (
    <ul className="nav nav-pills mt-2 mt-lg-4 mb-auto nav-menu">
      {MENU_ITEMS.map(
        ({ name, url, icon, addText, shortcut, addAction }, index) => (
          <li
            key={`sidebar-menu-${index}`}
            className={`nav-item w-100 position-relative`}
          >
            <Link
              to={url}
              className={`nav-link d-flex align-items-center position-relative ${
                isActive(url) ? "selected fw-semibold" : ""
              }`}
              aria-current={isActive(url) ? "page" : undefined}
              aria-label={name}
            >
              <i className={`d-flex bi ${icon} fs-5`}></i>
              <span className="ms-2">{name}</span>
            </Link>
            {addAction && (
              <OverlayTrigger
                placement="top"
                trigger={["hover", "focus"]}
                overlay={
                  <Tooltip id={`tooltip-add-${index}`}>
                    {addText} ({shortcut})
                  </Tooltip>
                }
              >
                <button
                  ref={(ref) => (buttonRefs.current[index] = ref)}
                  className="btn btn-sm h-min-auto position-absolute top-50 end-0 translate-middle-y me-2 opacity-0 hover-visible add-button btn-white"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Blur the button to hide tooltip
                    if (buttonRefs.current[index]) {
                      buttonRefs.current[index].blur();
                    }
                    addAction();
                  }}
                  onMouseLeave={() => {
                    // Ensure tooltip is hidden when mouse leaves
                    if (buttonRefs.current[index]) {
                      buttonRefs.current[index].blur();
                    }
                  }}
                >
                  <i className="bi bi-plus"></i>
                </button>
              </OverlayTrigger>
            )}
          </li>
        )
      )}
      <CollectionsMenu />

      <li className="nav-item w-100 mt-4 mb-1 px-3">
        <span className="menu-header">{t("sidebar.menu.discover", "Discover")}</span>
      </li>
      {DISCOVER_ITEMS.map(({ name, url, icon }, index) => (
        <li
          key={`discover-${index}`}
          className="nav-item w-100 position-relative"
        >
          <Link
            to={url}
            className={`nav-link d-flex align-items-center position-relative ${
              isActive(url) ? "selected fw-semibold" : ""
            }`}
            aria-current={isActive(url) ? "page" : undefined}
            aria-label={name}
          >
            <i className={`d-flex bi ${icon} fs-5`}></i>
            <span className="ms-2">{name}</span>
          </Link>
        </li>
      ))}
    </ul>
  );
};
